import React, { useState } from "react";

/**
 * Promotion entries — the feature list a brand promotes.
 *
 * One entry per feature. The owner writes three things and nothing else:
 *
 *   title        "Tank War"
 *   description  the VALUE, in their words — "play with friends, no ads, no
 *                payment, no algorithmic manipulation, no data collected"
 *   brief        the screenplay as PROSE — "log into infernastudio, open the
 *                loadout full screen, go back to tank mode, play a while, then
 *                cut to the highlights: unlocks, achievements, destruction"
 *
 * The brief is deliberately free text. A brand owner should not be writing CSS
 * selectors or step lists; compiling prose into a replayable screenplay is the
 * recorder's job, and the same brief can be recompiled when the product UI moves.
 *
 * A clip is recorded ONCE PER FEATURE and stored raw — no overlay, no sound.
 * The slogan and call to action are composed on afterwards, per post, so one
 * recording serves many promotions.
 */
const BLANK = { key: "", title: "", description: "", brief: "", enabled: true };

const slugify = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);

export default function PromotionEntries({ features = [], onChange, ctaUrl = "", ctaText = "", onMeta }) {
  const [draft, setDraft] = useState(BLANK);
  const [editingIdx, setEditingIdx] = useState(-1);
  const [error, setError] = useState("");

  const list = Array.isArray(features) ? features : [];

  const commit = () => {
    const title = draft.title.trim();
    if (!title) return setError("A title is needed — it names the feature being promoted.");
    if (!draft.description.trim())
      return setError("Describe the value this gives someone. The post is written from it.");
    // The key is the clip's cache identity: change it and the feature loses its
    // recording. So it is derived once, on create, and never re-derived on edit.
    const key = draft.key || slugify(title) || `feature-${Date.now()}`;
    const entry = { ...draft, key, title, description: draft.description.trim(), brief: draft.brief.trim() };
    const next = list.slice();
    if (editingIdx >= 0) next[editingIdx] = entry;
    else {
      if (next.some((f) => f.key === key))
        return setError("A feature with this name already exists — its clip would be overwritten.");
      next.push(entry);
    }
    onChange && onChange(next);
    setDraft(BLANK);
    setEditingIdx(-1);
    setError("");
  };

  const edit = (i) => { setDraft({ ...BLANK, ...list[i] }); setEditingIdx(i); setError(""); };
  const remove = (i) => {
    const next = list.slice();
    next.splice(i, 1);
    onChange && onChange(next);
    if (editingIdx === i) { setDraft(BLANK); setEditingIdx(-1); }
  };
  const toggle = (i) => {
    const next = list.slice();
    next[i] = { ...next[i], enabled: !next[i].enabled };
    onChange && onChange(next);
  };

  return (
    <div className="promotion-entries">
      <p className="text-muted" style={{ marginBottom: 12 }}>
        Each entry becomes its own promotion: a short product clip, recorded once and
        reused, with your slogan on top and a call to action underneath.
      </p>

      {list.length > 0 && (
        <ul className="list-unstyled" style={{ marginBottom: 16 }}>
          {list.map((f, i) => (
            <li key={f.key || i} style={{ border: "1px solid #e3e6ec", borderRadius: 10, padding: 12, marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <strong style={{ flex: 1 }}>{f.title || f.key}</strong>
                <label style={{ fontSize: 13, margin: 0 }}>
                  <input type="checkbox" checked={f.enabled !== false} onChange={() => toggle(i)} aria-label={`Promote ${f.title}`} />{" "}
                  Promote
                </label>
                <button type="button" className="btn btn-sm" onClick={() => edit(i)}>Edit</button>
                <button type="button" className="btn btn-sm" onClick={() => remove(i)}>Remove</button>
              </div>
              {f.description && <div style={{ fontSize: 13, color: "#5a6270", marginTop: 6 }}>{f.description}</div>}
              {!f.brief && (
                <div style={{ fontSize: 12, color: "#a06a00", marginTop: 6 }}>
                  No screenplay yet — this feature can be posted about, but has no clip to show.
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <div style={{ border: "1px dashed #cfd4dd", borderRadius: 10, padding: 12 }}>
        <label htmlFor="promo-title">Feature</label>
        <input id="promo-title" className="form-control" placeholder="Tank War"
          value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />

        <label htmlFor="promo-desc" style={{ marginTop: 10 }}>What it gives people</label>
        <textarea id="promo-desc" className="form-control" rows={3}
          placeholder="Play with friends. No ads, no payment, no algorithmic manipulation, and nothing collected but a nickname."
          value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />

        <label htmlFor="promo-brief" style={{ marginTop: 10 }}>
          What to record <span className="text-muted" style={{ fontWeight: 400 }}>— in plain words</span>
        </label>
        <textarea id="promo-brief" className="form-control" rows={4}
          placeholder="Log in, open the loadout full screen, go back to tank mode and play a while. Then cut to the highlights — unlocks, achievements, destruction, building."
          value={draft.brief} onChange={(e) => setDraft({ ...draft, brief: e.target.value })} />
        <small className="text-muted">
          Describe it the way you would to a person. No selectors or steps needed.
        </small>

        {error && <div style={{ color: "#b3261e", marginTop: 8, fontSize: 13 }} role="alert">{error}</div>}

        <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
          <button type="button" className="btn btn-primary btn-sm" onClick={commit}>
            {editingIdx >= 0 ? "Save changes" : "Add feature"}
          </button>
          {editingIdx >= 0 && (
            <button type="button" className="btn btn-sm" onClick={() => { setDraft(BLANK); setEditingIdx(-1); setError(""); }}>
              Cancel
            </button>
          )}
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <label htmlFor="promo-cta-text">Call to action</label>
        <input id="promo-cta-text" className="form-control" placeholder="Play free"
          value={ctaText} onChange={(e) => onMeta && onMeta({ ctaText: e.target.value })} />
        <label htmlFor="promo-cta-url" style={{ marginTop: 10 }}>Where it sends people</label>
        <input id="promo-cta-url" className="form-control" placeholder="https://infernastudio.com/games"
          value={ctaUrl} onChange={(e) => onMeta && onMeta({ ctaUrl: e.target.value })} />
        <small className="text-muted">Shown across the bottom of every clip. Leave blank to use the brand’s action link.</small>
      </div>
    </div>
  );
}
